import { FC, useContext, useState } from 'react';

import { Link, Separator } from '@smartshell/ui-kit';
import cn from 'classnames';

import { LocalizationContext } from 'localization';
import { ThemeContext } from 'theme';

import { FEATURES_CONFIG, LINKS_MAP } from './config';
import s from './FeatureHOC.module.scss';
import { InfoBlock } from './InfoBlock';
import { IFeatureHOCProps } from './interfaces';
import { useIsFeatureAvailable } from './useIsFeatureAvailable';

// @ts-ignore TODO: incorrect types, check with react types version update (issue: https://github.com/DefinitelyTyped/DefinitelyTyped/issues/18051)
export const FeatureHOC: FC<IFeatureHOCProps> = ({
  children,
  strategy = 'hide',
  fullWidth = true,
  name,
  nameKey,
  alias,
  fullHeight,
  grayBG,
  tooltipPosition = 'bottom-left',
  hideTitle,
  variant,
}) => {
  const { t, lang } = useContext(LocalizationContext);
  const isAvailable = useIsFeatureAvailable(alias);
  const [tooltipOpen, setIsTooltipOpen] = useState(false);
  const [timerID, setTimerID] = useState<NodeJS.Timeout | null>(null);
  const { theme } = useContext(ThemeContext);

  if (isAvailable) {
    return children;
  }

  if (strategy === 'hide') {
    return null;
  }

  const link = LINKS_MAP[alias]?.[lang];
  const description = t('feature.unavailable');

  if (strategy === 'violetReplace') {
    return (
      <InfoBlock feature={alias} hideTitle={hideTitle} variant={variant} />
    );
  }

  if (strategy === 'preview') {
    const { preview } = FEATURES_CONFIG[alias] ?? {};

    return (
      <div className={s.previewContainer}>
        <img
          src={preview?.[theme]}
          alt="feature preview"
          className={cn(s.preview)}
        />
        <InfoBlock feature={alias} absolute />
      </div>
    );
  }

  if (strategy === 'fade') {
    return (
      <div className={s.previewContainer}>
        <div className={s.fade} />
        <InfoBlock feature={alias} absolute centered />
        {children}
      </div>
    );
  }

  if (strategy === 'disable') {
    return (
      <div
        className={cn(s.disabled, fullWidth && s['full-width'])}
        onMouseEnter={() => {
          if (timerID) {
            clearTimeout(timerID);
            setTimerID(null);
          }

          setIsTooltipOpen(true);
        }}
        onMouseLeave={() => {
          if (timerID) {
            clearTimeout(timerID);
          }

          setTimerID(setTimeout(() => setIsTooltipOpen(false), 100));
        }}
      >
        <div className={s.overlay} />
        <div className={s.content}>
          {tooltipOpen && (
            <div className={cn(s.tooltip, s[tooltipPosition])}>
              {name && <span className={s.title}>{name}</span>}
              <span>{description}</span>
              {link && (
                <>
                  <Separator />
                  <Link title={t('more')} link={link} />
                </>
              )}
            </div>
          )}
        </div>
        {children}
      </div>
    );
  }

  if (strategy === 'replace') {
    return (
      <div
        className={cn(
          s.placeholder,
          fullHeight && s['full-height'],
          grayBG && s['gray-bg']
        )}
      >
        <div className={s.description}>
          {nameKey && <span className={s.title}>{t(nameKey)}</span>}
          {name && <span className={s.title}>{name}</span>}
          <span>{description}</span>
        </div>
        {link && <Link title={t('more')} link={link} />}
      </div>
    );
  }

  return children;
};
